import type { z } from "zod";
import {
  type CommandEnvelope,
  type CommandPayload,
  type CommandReceipt,
  type CommandType,
  Problem,
  type RouteName,
  routePath,
  routes,
} from "@probetalab/contracts";
import { uuidv7 } from "./uuid.js";

/**
 * Typed client over the route table in @probetalab/contracts. Every JSON response is validated with
 * the route's Zod schema, so a contract drift fails loudly in development instead of rendering
 * undefined. Cookies are first-party (Vite proxies /v1 in dev; same origin in production).
 */

export class ApiError extends Error {
  constructor(public readonly problem: Problem) {
    super(problem.detail ?? problem.title);
  }
}

type RouteOf<N extends RouteName> = (typeof routes)[N];
type ResponseOf<N extends RouteName> = z.infer<RouteOf<N>["response"]>;
type QueryOf<N extends RouteName> = RouteOf<N> extends { query: infer Q extends z.ZodTypeAny } ? z.input<Q> : never;

const DEVICE_KEY = "probeta.device_id";

/** A stable per-browser id, sent as X-Device-Id until device enrolment exists (ADR 0013). */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = uuidv7();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

async function toProblem(res: Response): Promise<ApiError> {
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* non-JSON body */
  }
  const parsed = Problem.safeParse(data);
  return new ApiError(parsed.success ? parsed.data : { type: "about:blank", title: res.statusText || "Error", status: res.status });
}

function headers(body?: unknown, accept = "application/json, application/problem+json"): HeadersInit {
  return {
    Accept: accept,
    "X-Device-Id": deviceId(),
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
  };
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, { method, credentials: "same-origin", headers: headers(body), body: body !== undefined ? JSON.stringify(body) : undefined });
  if (!res.ok) throw await toProblem(res);
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

export function withQuery(path: string, query?: Record<string, unknown>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function urlFor<N extends RouteName>(name: N, opts: { params?: Record<string, string>; query?: Record<string, unknown> } = {}): string {
  return withQuery(routePath(routes[name].path, opts.params), opts.query);
}

export async function get<N extends RouteName>(name: N, opts: { params?: Record<string, string>; query?: QueryOf<N> } = {}): Promise<ResponseOf<N>> {
  const route = routes[name];
  const url = withQuery(routePath(route.path, opts.params), opts.query as Record<string, unknown> | undefined);
  const data = await request<unknown>("GET", url);
  return route.response.parse(data) as ResponseOf<N>;
}

export async function post<N extends RouteName>(name: N, body?: unknown, opts: { params?: Record<string, string> } = {}): Promise<ResponseOf<N>> {
  const route = routes[name];
  const data = await request<unknown>("POST", routePath(route.path, opts.params), body);
  return route.response.parse(data) as ResponseOf<N>;
}

/** text/html routes (printable tickets and labels). */
export async function getHtml<N extends RouteName>(name: N, opts: { params?: Record<string, string>; query?: Record<string, unknown> } = {}): Promise<string> {
  const res = await fetch(urlFor(name, opts), { credentials: "same-origin", headers: headers(undefined, "text/html, application/problem+json") });
  if (!res.ok) throw await toProblem(res);
  return res.text();
}

/** Binary routes: application/pdf by default, or the image types of the instruction images. */
export async function getBlob<N extends RouteName>(
  name: N,
  opts: { params?: Record<string, string>; query?: Record<string, unknown> } = {},
  accept = "application/pdf",
): Promise<Blob> {
  const res = await fetch(urlFor(name, opts), { credentials: "same-origin", headers: headers(undefined, `${accept}, application/problem+json`) });
  if (!res.ok) throw await toProblem(res);
  return res.blob();
}

/** Send a command. The id is a device-generated UUIDv7 and doubles as the idempotency key. */
export async function command<T extends CommandType>(type: T, payload: CommandPayload<T>, opts: { branch_id?: string; id?: string } = {}): Promise<CommandReceipt> {
  const envelope: CommandEnvelope = {
    id: opts.id ?? uuidv7(),
    type,
    payload,
    occurred_at: new Date().toISOString(),
    device_id: deviceId(),
    ...(opts.branch_id ? { branch_id: opts.branch_id } : {}),
  };
  const receipt = await post("commands", envelope);
  if (receipt.outcome === "rejected") {
    const e = receipt.error;
    throw new ApiError({ type: e?.type ?? "about:blank", title: e?.title ?? "Rechazado", status: 409, detail: e?.detail });
  }
  return receipt;
}

/** Send a command and return its typed result. */
export async function run<T extends CommandType, R>(type: T, payload: CommandPayload<T>, opts: { branch_id?: string } = {}): Promise<R> {
  const receipt = await command(type, payload, opts);
  return receipt.result as R;
}
